import type { PrescriptionTemplate, PrescriptionTemplateGroup } from "@/lib/queries";

// Prescription-template grouping (migration 0036, 2026-11-02).
//
// Shared by the /prescription-templates management page and the EMR visit
// page's PrescriptionTemplatePickerDialog: both render the library as
// collapsible per-group accordions with "Ungrouped" pinned last.

/** Accordion value for the pseudo-group holding templates with groupId null. */
export const UNGROUPED_KEY = "__ungrouped__";
export const UNGROUPED_LABEL = "Ungrouped";

export interface TemplateBucket {
  /** Accordion value: group id, or UNGROUPED_KEY for the pseudo-group. */
  key: string;
  /** Display name: group name, or UNGROUPED_LABEL. */
  name: string;
  /** The real groupId to PATCH onto a template moved here (null = ungroup). */
  groupId: string | null;
  items: PrescriptionTemplate[];
}

/**
 * Bucket templates under their groups, alphabetically, "Ungrouped" last
 * (the investigations-page pattern). Empty groups are HIDDEN — an accordion
 * with no children is noise in the picker.
 */
export function bucketTemplates(
  templates: PrescriptionTemplate[],
  groups: PrescriptionTemplateGroup[],
): TemplateBucket[] {
  const byGroup = new Map<string, PrescriptionTemplate[]>();
  for (const g of groups) byGroup.set(g.id, []);
  byGroup.set(UNGROUPED_KEY, []);
  for (const t of templates) {
    const key = t.groupId && byGroup.has(t.groupId) ? t.groupId : UNGROUPED_KEY;
    byGroup.get(key)!.push(t);
  }
  return [
    ...groups.map((g) => ({ key: g.id, name: g.name, groupId: g.id, items: byGroup.get(g.id)! })),
    { key: UNGROUPED_KEY, name: UNGROUPED_LABEL, groupId: null, items: byGroup.get(UNGROUPED_KEY)! },
  ].filter((b) => b.items.length > 0);
}